import { defineStore } from 'pinia';
import type { UserInfo, UserRole, AuthResponse } from '@trace/shared/types';
import { AUTH_STORAGE_KEYS } from '@trace/shared/constants';

interface AuthState {
  token: string | null;
  user: UserInfo | null;
  permissions: string[];
  tenantId: string | null;
  enterpriseId: number | string | null;
  enterpriseCode: string | null;
  /**
   * 超管选中的代工企业筛选视角。
   * 一律用字符串存：企业主键是 19 位雪花 ID，转成 number 会被 JS 双精度舍入，
   * 带着这个坏 ID 去查库会「一条都查不到」。
   */
  filterEnterpriseId: string | 'all';
  isAuthenticated: boolean;
}

export const useAuthStore = defineStore('auth', {
  state: (): AuthState => {
    // C-3: Try restoring from sessionStorage (fallback to localStorage for migration)
    const saved = sessionStorage.getItem(AUTH_STORAGE_KEYS.token) || localStorage.getItem(AUTH_STORAGE_KEYS.token);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (parsed.token) {
          return {
            token: parsed.token,
            user: parsed.user || null,
            permissions: parsed.permissions || ['*'],
            tenantId: parsed.tenantId || 'tenant_wuchang_001',
            enterpriseId: parsed.enterpriseId || parsed.user?.enterpriseId || null,
            enterpriseCode: parsed.enterpriseCode || parsed.user?.enterpriseCode || null,
            filterEnterpriseId: parsed.filterEnterpriseId || 'all',
            isAuthenticated: true,
          };
        }
      } catch (err) {
        console.warn('解析会话存储凭据失败:', err);
      }
    }

    return {
      token: null,
      user: null,
      permissions: [],
      tenantId: null,
      enterpriseId: null,
      enterpriseCode: null,
      filterEnterpriseId: 'all',
      isAuthenticated: false,
    };
  },

  getters: {
    userName: (state) => state.user?.realName || state.user?.username || '管理员',
    userRoleName: (state) => state.user?.roleName || '系统管理员',
    isSuperAdmin: (state) => state.user?.role === 'admin' || state.user?.dataScope === 'all',
    effectiveEnterpriseId: (state): number | string | null => {
      if (state.user?.role === 'admin' || state.user?.dataScope === 'all') {
        return state.filterEnterpriseId !== 'all' ? state.filterEnterpriseId : null;
      }
      return state.user?.enterpriseId || state.enterpriseId || null;
    },
  },

  actions: {
    saveToStorage() {
      // C-3: 使用 sessionStorage 减少 Token 明文持久化暴露风险，并清理 localStorage 遗留数据
      sessionStorage.setItem(
        AUTH_STORAGE_KEYS.token,
        JSON.stringify({
          token: this.token,
          user: this.user,
          permissions: this.permissions,
          tenantId: this.tenantId,
          enterpriseId: this.enterpriseId,
          enterpriseCode: this.enterpriseCode,
          filterEnterpriseId: this.filterEnterpriseId,
          isAuthenticated: this.isAuthenticated,
        }),
      );
      localStorage.removeItem(AUTH_STORAGE_KEYS.token);
    },

    login(authData: AuthResponse) {
      this.token = authData.token;
      this.user = authData.user;
      this.permissions = authData.user.permissions || ['*'];
      this.tenantId = authData.user.tenantId || 'tenant_wuchang_001';
      this.enterpriseId = authData.user.enterpriseId || null;
      this.enterpriseCode = authData.user.enterpriseCode || null;
      this.filterEnterpriseId = authData.user.enterpriseId ? String(authData.user.enterpriseId) : 'all';
      this.isAuthenticated = true;
      this.saveToStorage();
    },

    setFilterEnterpriseId(id: string | number | 'all') {
      this.filterEnterpriseId = id === 'all' ? 'all' : String(id);
      this.saveToStorage();
    },

    logout() {
      this.token = null;
      this.user = null;
      this.permissions = [];
      this.tenantId = null;
      this.enterpriseId = null;
      this.enterpriseCode = null;
      this.filterEnterpriseId = 'all';
      this.isAuthenticated = false;
      sessionStorage.removeItem(AUTH_STORAGE_KEYS.token);
      localStorage.removeItem(AUTH_STORAGE_KEYS.token);
    },

    updateUser(updatedUser: Partial<UserInfo>) {
      if (!this.user) return;
      this.user = { ...this.user, ...updatedUser };
      if (updatedUser.enterpriseId !== undefined) {
        this.enterpriseId = updatedUser.enterpriseId;
      }
      if (updatedUser.enterpriseCode !== undefined) {
        this.enterpriseCode = updatedUser.enterpriseCode;
      }
      this.saveToStorage();
    },

    async switchRole(targetRole: UserRole) {
      // C-4: 消除原生 fetch 与硬编码魔法值，使用统一 API 客户端拉取最新数据
      const { get: apiGet } = await import('@/api/request');

      // 1. 动态拉取当前系统最新的角色矩阵列表
      let targetRoleConfig: any = null;
      try {
        const rolesList = await apiGet<any[]>('/system/roles');
        if (Array.isArray(rolesList)) {
          targetRoleConfig = rolesList.find((r: any) => r.role === targetRole || r.roleKey === targetRole);
        }
      } catch (err) {
        console.warn('获取最新角色权限矩阵失败，将使用现有会话回退', err);
      }

      // 2. 解析动态权限
      let permissions: string[] = ['*'];
      if (targetRole !== 'admin') {
        const rawMenus = targetRoleConfig?.allowedMenus;
        if (Array.isArray(rawMenus)) {
          permissions = rawMenus;
        } else if (typeof rawMenus === 'string') {
          try {
            const parsed = JSON.parse(rawMenus);
            permissions = Array.isArray(parsed) ? parsed : [];
          } catch {
            permissions = [];
          }
        } else {
          permissions = [];
        }
      }

      // 3. 动态拉取对应角色的真实用户身份信息 (消除硬编码企业ID与用户名)
      let targetUser: any = null;
      try {
        const usersResp = await apiGet<{ list: UserInfo[]; total: number }>('/system/users', {
          role: targetRole,
          status: 'active',
          pageSize: 20,
        });
        const userList = usersResp?.list || (Array.isArray(usersResp) ? (usersResp as any) : []);
        targetUser = userList.find((u: any) => u.role === targetRole) || userList[0];
      } catch (err) {
        console.warn('获取角色对应真实用户信息失败', err);
      }

      const roleName = targetRoleConfig?.roleName || targetUser?.roleName || (
        targetRole === 'admin' ? '超级管理员' :
        targetRole === 'manager' ? '生产厂长' :
        targetRole === 'oem' ? 'OEM代工厂商' : '系统用户'
      );
      const dataScope = targetRoleConfig?.dataScope || (targetRole === 'admin' ? 'all' : 'tenant');
      const enterpriseId = targetUser?.enterpriseId ?? null;
      const enterpriseCode = targetUser?.enterpriseCode ?? null;

      // 4. 重构当前用户信息与 Token
      const userId = targetUser?.id || (this.user ? this.user.id : `usr_${targetRole}`);
      this.token = `mock_jwt_token_${targetRole}_${userId}_${Date.now()}`;
      this.user = {
        id: userId,
        username: targetUser?.username || (targetRole === 'admin' ? 'admin' : `user_${targetRole}`),
        realName: targetUser?.realName || `${roleName}用户`,
        role: targetRole,
        roleName,
        avatar: targetUser?.avatar || (
          targetRole === 'admin' ? 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=100' :
          targetRole === 'manager' ? 'https://images.unsplash.com/photo-1570295999919-56ceb5ecca61?w=100' :
          'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100'
        ),
        permissions,
        dataScope,
        tenantId: targetUser?.tenantId || this.tenantId || 'tenant_wuchang_001',
        tenantName: targetUser?.tenantName || '黑龙江工程北米农业科技有限公司',
        enterpriseId,
        enterpriseCode,
        department: targetUser?.department || targetRoleConfig?.description || '农业供应链中心',
        phone: targetUser?.phone || '',
        email: targetUser?.email || '',
        status: 'active',
        createdAt: targetUser?.createdAt || (this.user?.createdAt || new Date().toISOString()),
        updatedAt: new Date().toISOString(),
      };

      this.permissions = permissions;
      this.enterpriseId = enterpriseId;
      this.enterpriseCode = enterpriseCode;
      this.filterEnterpriseId = targetRole === 'admin' ? 'all' : (enterpriseId ? String(enterpriseId) : 'all');
      this.saveToStorage();
    },

    hasPermission(permissionCode: string): boolean {
      if (this.permissions.includes('*')) return true;
      if (this.permissions.includes(permissionCode)) return true;

      const [module] = permissionCode.split(':');
      if (module && (this.permissions.includes(`${module}:*`) || this.permissions.includes(module))) return true;

      return false;
    },

    hasRole(roles: UserRole | UserRole[]): boolean {
      if (!this.user) return false;
      const targetRoles = Array.isArray(roles) ? roles : [roles];
      return targetRoles.includes(this.user.role);
    },
  },
});
